import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { AppState } from 'src/app/core/app.state';
import { LocalStorageService } from 'src/app/shared/localstorage.service';
import { AuthResponse } from '../models/auth-response';
import { AuthService } from '../services/auth.service';
import {
  ForgetPasswordAction,
  ForgetPasswordError,
  LoginAction,
  LoginActionError,
  LoginActionSuccess,
  ResetPasswordAction,
  ResetPasswordError,
  SetAuthLoading,
  UnsubscribeAction,
  UnsubscribeActionError,
  UnsubscribeActionSuccess,
} from './auth.actions';
import { SetLoading } from 'src/app/shared/store/shared.actions';
import { APIResponse } from 'src/app/core/models/api-response';
import { CommonService } from 'src/app/shared/common.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastMessages } from 'src/app/shared/enums/shared.enums';

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  store = inject<Store<AppState>>(Store);
  private authService = inject(AuthService);
  private router = inject(Router);
  private commonService = inject(CommonService);
  private translateService = inject(TranslateService);

  LoginEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginAction),
      switchMap((action) =>
        this.authService.login(action.payload).pipe(
          map((payload: APIResponse<AuthResponse>) => LoginActionSuccess({ payload })),
          tap((res: any) => {
            (LocalStorageService.put('currentUser', res.payload.object),
              this.router.navigate(['/catalogs']),
              this.store.dispatch(SetLoading({ isAppLoading: false })));
          }),
          catchError(() =>
            of(LoginActionError()).pipe(
              tap(() =>
                this.commonService.showToast(
                  this.translateService.instant(`auth_toast.${ToastMessages.CredentialsError}`),
                  'ERROR'
                )
              )
            )
          ),
          finalize(() => {
            this.store.dispatch(SetLoading({ isAppLoading: false }));
          })
        )
      ),
      finalize(() => {})
    )
  );

  ForgetPasswordEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForgetPasswordAction),
      switchMap((action) =>
        this.authService.forgetPassword(action.payload).pipe(
          map(() => {
            this.commonService.showToast(
              this.translateService.instant(`auth_toast.${ToastMessages.PasswordResetEmailSent}`),
              'SUCCESS'
            );
            this.store.dispatch(SetAuthLoading({ isLoading: false }));
            return { type: '[Auth] Forget Password Success' };
          }),
          catchError((error) =>
            of(ForgetPasswordError({ error: error.message || 'An error occurred' })).pipe(
              tap(() => {
                this.commonService.showToast(
                  this.translateService.instant(`auth_toast.${ToastMessages.PasswordResetError}`),
                  'ERROR'
                );
                this.store.dispatch(SetAuthLoading({ isLoading: false }));
              })
            )
          ),
          finalize(() => {
            this.store.dispatch(SetLoading({ isAppLoading: false }));
          })
        )
      )
    )
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResetPasswordAction),
      switchMap((action) =>
        this.authService
          .resetPassword(
            action.payload.newPassword,
            action.payload.confirm_password,
            action.payload.token
          )
          .pipe(
            map(() => {
              this.commonService.showToast(
                this.translateService.instant(`auth_toast.${ToastMessages.PasswordResetSuccess}`),
                'SUCCESS'
              );
              return SetAuthLoading({ isLoading: false });
            }),
            tap(() => {
              this.store.dispatch(SetAuthLoading({ isLoading: false }));
              this.router.navigate(['']);
            }),
            catchError((error) =>
              of(ResetPasswordError({ error: error.message || 'An error occurred' })).pipe(
                tap(() => {
                  this.commonService.showToast(
                    this.translateService.instant(
                      `auth_toast.${ToastMessages.PasswordResetFailed}`
                    ),
                    'ERROR'
                  );
                  this.store.dispatch(SetAuthLoading({ isLoading: false }));
                })
              )
            ),
            finalize(() => {
              this.store.dispatch(SetLoading({ isAppLoading: false }));
            })
          )
      )
    )
  );

  Unsubscribe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UnsubscribeAction),
      switchMap((action) =>
        this.authService.unsubscribe(action.contact_id).pipe(
          map(() => UnsubscribeActionSuccess()),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`auth_toast.${ToastMessages.UnsubscribeSuccess}`),
              'SUCCESS'
            );
            this.router.navigate(['/pages/catalogs']);
          }),
          catchError(() =>
            of(UnsubscribeActionError()).pipe(
              tap(() => {
                this.commonService.showToast(
                  this.translateService.instant(`auth_toast.${ToastMessages.UnsubscribeFailed}`),
                  'ERROR'
                );
                this.store.dispatch(SetLoading({ isAppLoading: false }));
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(SetLoading({ isAppLoading: false }));
      })
    )
  );
}
